import React, { useCallback, useRef, useState } from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { clienteService } from '../../../../clientes/services/clienteService';

export interface OpcionCliente {
  id: string;
  razonSocial: string;
  nit: string | null;
}

interface BuscadorClientesProps {
  valor?: OpcionCliente | null;
  onSeleccionar: (cliente: OpcionCliente | null) => void;
  deshabilitado?: boolean;
  ocupado?: boolean;
  testId?: string;
}

// SPEC-103 — buscador de clientes activos por razón social o NIT (debounce de 300 ms). SPEC-206
// lo extrae de ClientBlock para reutilizarlo en "Duplicar cotización" (design §7b).
export const BuscadorClientes: React.FC<BuscadorClientesProps> = ({ valor, onSeleccionar, deshabilitado, ocupado, testId }) => {
  const [opciones, setOpciones] = useState<OpcionCliente[]>([]);
  const [buscando, setBuscando] = useState(false);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const buscar = useCallback((busqueda: string) => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    if (!busqueda.trim()) {
      setOpciones([]);
      return;
    }
    debounceTimer.current = setTimeout(async () => {
      setBuscando(true);
      try {
        const resultado = await clienteService.listar({ busqueda, activo: true, limite: 20 });
        setOpciones(resultado.data);
      } finally {
        setBuscando(false);
      }
    }, 300);
  }, []);

  return (
    <Autocomplete
      data-testid={testId}
      options={valor && !opciones.some((o) => o.id === valor.id) ? [valor, ...opciones] : opciones}
      value={valor === undefined ? undefined : valor}
      loading={buscando}
      getOptionLabel={(opcion) => (opcion.nit ? `${opcion.razonSocial} — NIT ${opcion.nit}` : opcion.razonSocial)}
      isOptionEqualToValue={(opcion, v) => opcion.id === v.id}
      onInputChange={(_e, texto, razon) => {
        if (razon === 'input') buscar(texto);
      }}
      onChange={(_e, seleccionado) => onSeleccionar(seleccionado)}
      disabled={deshabilitado}
      sx={{ marginTop: '10px' }}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          placeholder="Buscar por razón social o NIT…"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {buscando || ocupado ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};
